import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import LogoAMP from "../../assets/img/logo white.png";
import LogoAMPDark from "../../assets/img/logo colorize.png";
import Hamburger_menu from "../../assets/common/hamburger-menu.svg";
import Close from "../../assets/common/icons8-close.svg";
import ArrowIcon from "../../assets/common/arrow-down-svgrepo-com.svg";

import searchIcon from "../../assets/icon/search.svg";

import { useEffect, useState } from "react";
import { getCategories } from "../../api/categoryApi";
import { useLanguage } from "../../context/LanguageContext";

interface Category {
  id: number;
  slug: string;
  categoryName?: string;
  category_name?: string;
}

// Ambil nama kategori terlepas dari apakah API balikin camelCase atau
// snake_case -- jaga-jaga backend belum konsisten transform-nya.
const getCategoryName = (item: Category) =>
  item.categoryName ?? item.category_name ?? "";

/**
 * Label menu navigasi statis (BUKAN dari CMS -- section `header` CMS
 * sengaja tidak dipakai di sini, lihat api/homePageApi.ts). Diterjemahkan
 * manual di sini karena jumlahnya kecil dan tidak pernah admin-editable.
 */
const NAV_LABELS = {
  id: {
    home: "Home",
    about: "Tentang Kami",
    products: "Produk",
    project: "Proyek",
    contact: "Kontak",
    search: "Cari produk",
  },
  en: {
    home: "Home",
    about: "About Us",
    products: "Products",
    project: "Project",
    contact: "Contact",
    search: "Search product",
  },
} as const;

/** Toggle ID/EN untuk seluruh website. Dipakai di desktop nav dan mobile sidebar. */
function LanguageSwitcher({ scrolled }: { scrolled: boolean }) {
  const { language, setLanguage } = useLanguage();

  const inactiveClass = scrolled ? "text-slate-500" : "text-white/70";

  return (
    <div
      role="group"
      aria-label="Language / Bahasa"
      className={`flex items-center rounded-full border p-0.5 text-xs font-bold transition-colors duration-300 ${
        scrolled ? "border-[#324A5A33]" : "border-white/40"
      }`}>
      <button
        type="button"
        onClick={() => setLanguage("id")}
        aria-pressed={language === "id"}
        className={`rounded-full px-2.5 py-1 transition-colors ${
          language === "id" ? "bg-[#3DA4A8] text-white" : inactiveClass
        }`}>
        ID
      </button>
      <button
        type="button"
        onClick={() => setLanguage("en")}
        aria-pressed={language === "en"}
        className={`rounded-full px-2.5 py-1 transition-colors ${
          language === "en" ? "bg-[#3DA4A8] text-white" : inactiveClass
        }`}>
        EN
      </button>
    </div>
  );
}

// Diekstrak dari 4x blok className yang sebelumnya di-copy-paste identik
// persis untuk tiap NavLink (Home/About/Project/Contact) -- sekarang
// cukup 1 sumber kebenaran, tidak rawan drift kalau ada yang diedit.
const desktopNavLinkClass =
  (scrolled: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    `
  relative
  transition-all
  duration-500

  ${
    scrolled
      ? isActive
        ? "text-[#324A5A]"
        : "text-slate-500 hover:text-[#324A5A]"
      : isActive
        ? "text-[#7ae9ed]"
        : "text-white hover:text-[#D8EEF0]"
  }

  after:absolute
  after:left-1/2
  after:-translate-x-1/2
  after:-bottom-1
  after:h-[2px]
  after:rounded-full

  ${scrolled ? "after:bg-[#324A5A]" : "after:bg-[#63BEC2]"}

  after:transition-all
  after:duration-500

  ${isActive ? "after:w-full" : "after:w-0"}
`;

const mobileNavLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center rounded-2xl px-4 py-3.5 text-[15px] font-semibold transition-all duration-300 ${
    isActive
      ? "bg-gradient-to-r from-[#3DA4A8] to-[#2C4858] text-white shadow-lg shadow-[#3DA4A8]/30"
      : "text-slate-600 hover:bg-slate-50 hover:text-[#2C4858] hover:pl-5"
  }`;

const mobileMenuItemVariants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0 },
};

export default function Nav() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const { language } = useLanguage();
  const labels = NAV_LABELS[language];

  const [categories, setCategories] = useState<Category[]>([]);

  const loadCategories = async () => {
    const result = await getCategories({
      page: 1,
      limit: 5, // dinaikkan dari 10 -- kategori kamu bisa lebih dari 10
    });

    setCategories(result.data.categoryList);
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    // Throttle pakai requestAnimationFrame -- handleScroll cuma jalan
    // maksimal sekali per frame render, bukan puluhan kali per detik
    // tiap event scroll native ke-fire.
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSelectCategory = (slug: string) => {
    navigate(`/products/${slug}`);
    setIsOpen(false); // fix: sebelumnya sidebar mobile tidak ke-close di sini
  };

  return (
    <>
      {/* ================= NAVBAR ================= */}
      <nav
        className={`
          fixed
          top-0

          left-1/2
          -translate-x-1/2

          z-50

          w-full
         

          h-[60px]

          border

          transition-all
          duration-300

          ${
            scrolled
              ? `
                bg-white/85
                backdrop-blur-md

                border-[#324A5A1A]

                shadow-lg
              `
              : `
                bg-black/50

                border-[#FFFFFF1A]

                backdrop-blur-md

                lg:bg-white/10
                lg:backdrop-blur-[25.8px]
              `
          }
        `}>
        <div
          className="
            h-full

            flex
            items-center
            justify-between

            px-8
          ">
          {/* ================= LOGO ================= */}
          <Link to="/" className="flex cursor-pointer items-center gap-4">
            <img
              src={scrolled ? LogoAMPDark : LogoAMP}
              alt="logo"
              className="h-[67px] w-[245px] object-contain"
            />
          </Link>

          {/* ================= DESKTOP MENU ================= */}
          <div
            className={`
              hidden
              md:flex

              items-center
              gap-12

              font-semibold

              transition-colors
              duration-300

              ${scrolled ? "text-[#243B5A]" : "text-white"}
            `}>
            <NavLink to="/Home" className={desktopNavLinkClass(scrolled)}>
              {labels.home}
            </NavLink>

            <NavLink to="/About" className={desktopNavLinkClass(scrolled)}>
              {labels.about}
            </NavLink>

            {/* ================= DROPDOWN ================= */}
            <div className="relative group">
              <div
                className={`
                flex
                items-center
                gap-2

                cursor-pointer

                transition-all

                ${scrolled ? "hover:text-[#3DA4A8]" : "hover:text-[#D8EEF0]"}
              `}>
                <span
                  className={`transition-colors duration-200 ${
                    scrolled
                      ? "text-slate-500 hover:text-[#324A5A]"
                      : "text-white hover:text-[#D8EEF0]"
                  }`}>
                  {labels.products}
                </span>

                <img
                  src={ArrowIcon}
                  alt=""
                  className={`
                  h-4
                  w-4
                  transition-all
                  duration-300
                  group-hover:rotate-180
                  ${scrolled ? "" : "invert"}
                `}
                />
              </div>

              {/* Dropdown */}
              <div
                className="
                absolute
                top-[42px]
                left-1/2
                -translate-x-1/2

                invisible
                opacity-0
                translate-y-2

                group-hover:visible
                group-hover:opacity-100
                group-hover:translate-y-0

                transition-all
                duration-300

                w-[240px]

                bg-white/70
                backdrop-blur-xl

                rounded-b-[32px]

                shadow-xl

                overflow-hidden

                z-50
              ">
                {categories
                  .filter(
                    (item) =>
                      item.categoryName === "Seal Product" ||
                      item.categoryName === "Mechanical Seal" ||
                      item.categoryName === "Service Hydraulic" ||
                      item.categoryName === "Custom Product",
                  )
                  .map((item) => (
                    <button
                      type="button"
                      onClick={() => navigate(`/products/${item.slug}`)}
                      key={item.id}
                      className="
        flex
        w-full
        items-center
        justify-between
        px-6
        py-4
        cursor-pointer
        text-left
        text-[#324A5A]
        font-semibold
        hover:bg-white/40
        focus-visible:outline-none
        focus-visible:bg-white/40
      ">
                      <span>{getCategoryName(item)}</span>
                    </button>
                  ))}
              </div>
            </div>

            <NavLink to="/Project" className={desktopNavLinkClass(scrolled)}>
              {labels.project}
            </NavLink>

            <NavLink to="/Contact" className={desktopNavLinkClass(scrolled)}>
              {labels.contact}
            </NavLink>

            <NavLink to="/ProductSearch" aria-label={labels.search}>
              <img
                src={searchIcon}
                alt=""
                className={`
                w-6
                h-6
                ${scrolled ? "" : "invert"}
              `}
              />
            </NavLink>

            <LanguageSwitcher scrolled={scrolled} />
          </div>

          {/* ================= MOBILE BUTTON ================= */}
          <button
            onClick={() => setIsOpen(true)}
            className="md:hidden"
            aria-label="Open menu">
            <img
              src={Hamburger_menu}
              alt=""
              className={`
                h-7
                w-7

                transition-all
                duration-300

                ${scrolled ? "" : "invert"}
              `}
            />
          </button>
        </div>
      </nav>

      {/* ================= MOBILE SIDEBAR (modern, animated) ================= */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[998] bg-gradient-to-br from-[#2C4858]/70 via-black/50 to-[#3DA4A8]/30 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="fixed top-0 right-0 z-[999] flex h-screen w-[340px] flex-col bg-white/95 shadow-[0_0_60px_rgba(44,72,88,0.35)] backdrop-blur-2xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-6">
                <Link to="/" className="flex cursor-pointer items-center gap-4">
                  <img
                    src={scrolled ? LogoAMPDark : LogoAMP}
                    alt="logo"
                    className="h-[67px] w-[245px] object-contain"
                  />
                </Link>

                <motion.button
                  whileHover={{ scale: 1.08, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 transition-colors hover:bg-[#324A5A]">
                  <img src={Close} className="h-4 w-4" alt="" />
                </motion.button>
              </div>

              {/* Menu */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
                  },
                }}
                className="mt-6 flex flex-1 flex-col space-y-1.5 overflow-y-auto px-5">
                <motion.div
                  variants={mobileMenuItemVariants}
                  className="mb-3 flex justify-end">
                  <LanguageSwitcher scrolled />
                </motion.div>

                <motion.div variants={mobileMenuItemVariants}>
                  <NavLink
                    to="/Home"
                    onClick={() => setIsOpen(false)}
                    className={mobileNavLinkClass}>
                    {labels.home}
                  </NavLink>
                </motion.div>

                <motion.div variants={mobileMenuItemVariants}>
                  <NavLink
                    to="/About"
                    onClick={() => setIsOpen(false)}
                    className={mobileNavLinkClass}>
                    {labels.about}
                  </NavLink>
                </motion.div>

                {/* Product Dropdown */}
                <motion.div variants={mobileMenuItemVariants}>
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(!openDropdown)}
                    aria-expanded={openDropdown}
                    className={`flex w-full items-center justify-between rounded-2xl px-4 py-3.5 text-[15px] font-semibold transition-all duration-300 ${
                      openDropdown
                        ? "bg-[#EAF6F6] text-[#2C4858]"
                        : "text-slate-600 hover:bg-slate-50 hover:text-[#2C4858]"
                    }`}>
                    <span>{labels.products}</span>
                    <motion.img
                      src={ArrowIcon}
                      alt=""
                      animate={{ rotate: openDropdown ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="h-3.5 w-3.5"
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {openDropdown && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden">
                        <div className="ml-3 mb-1 mt-2 space-y-1 border-l-2 border-[#3DA4A8]/30 py-1 pl-4">
                          {categories
                            .filter(
                              (item) =>
                                item.categoryName === "Seal Product" ||
                                item.categoryName === "Mechanical Seal" ||
                                item.categoryName === "Service Hydraulic" ||
                                item.categoryName === "Custom Product",
                            )
                            .map((item) => (
                              <button
                                type="button"
                                key={item.id}
                                onClick={() => handleSelectCategory(item.slug)}
                                className="group flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2.5 text-left text-[14px] text-slate-500 transition-all duration-200 hover:translate-x-1 hover:bg-[#D8EEF0]/60 hover:text-[#2C4858]">
                                <span className="h-1.5 w-1.5 rounded-full bg-[#3DA4A8]/40 transition-colors group-hover:bg-[#3DA4A8]" />
                                {getCategoryName(item)}
                              </button>
                            ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                <motion.div variants={mobileMenuItemVariants}>
                  <NavLink
                    to="/Project"
                    onClick={() => setIsOpen(false)}
                    className={mobileNavLinkClass}>
                    {labels.project}
                  </NavLink>
                </motion.div>

                <motion.div variants={mobileMenuItemVariants}>
                  <NavLink
                    to="/Contact"
                    onClick={() => setIsOpen(false)}
                    className={mobileNavLinkClass}>
                    {labels.contact}
                  </NavLink>
                </motion.div>

                <motion.div variants={mobileMenuItemVariants}>
                  <NavLink
                    to="/ProductSearch"
                    onClick={() => setIsOpen(false)}
                    className={mobileNavLinkClass}>
                    {labels.search}
                  </NavLink>
                </motion.div>
              </motion.div>

              {/* Footer accent */}
              <div className="border-t border-slate-100 px-6 py-5">
                <p className="text-xs text-slate-400">
                  PT Arunika Medina Pratama
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
